import {
  LANGUAGES,
  MEDIA_CHANNELS,
  MAPPING_KEYS_STATUSES,
  BUYERS_STATUSES,
} from "../constants";

export const isDevelopment = process.env.NODE_ENV === "development";

export const noop = () => {};

export const debug = (() => {
  const methods = {
    log: noop,
    info: noop,
    warn: noop,
    error: noop,
  };

  if (isDevelopment) {
    Object.assign(methods, {
      log: console.log.bind(
        console,
        "%cLOG",
        "color: white; background-color: grey; padding: 2px 4px",
      ),
      info: console.info.bind(
        console,
        "%cINFO",
        "color: white; background-color: blue; padding: 2px 4px",
      ),
      warn: console.warn.bind(
        console,
        "%cWARN",
        "color: black; background-color: orange; padding: 2px 4px",
      ),
      error: console.error.bind(
        console,
        "%cERROR",
        "color: white; background-color: red; padding: 2px 4px",
      ),
    });
  }

  return methods;
})();

export const initLocalization = (strings) => {
  strings.setLanguage(localStorage.getItem("lang") ?? LANGUAGES.fr);
};

export const toLocalDateTime = (d) =>
  new Date(d).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

export const isXlsxFile = (file) => /\.xlsx$/i.test(file.name);

export const createSocialPageUrl = (mediaChannel, accountName) => {
  switch (mediaChannel) {
    case MEDIA_CHANNELS.tiktok:
      return `https://${mediaChannel.toLowerCase()}.com/@${accountName
        .toLowerCase()
        .replace(/ /g, "")}`;

    case MEDIA_CHANNELS.snapchat:
      return `https://${mediaChannel.toLowerCase()}.com/add/${accountName
        .toLowerCase()
        .replace(/ /g, "")}`;

    case MEDIA_CHANNELS.linkedin:
      return `https://${mediaChannel.toLowerCase()}.com`;

    default:
      return `https://${mediaChannel.toLowerCase()}.com/${accountName
        .toLowerCase()
        .replace(/ /g, "")}`;
  }
};

export const createMappingStatusClasses = (row, value) => {
  const productDefaultClass = "product-default";

  const { isLocked, isLockedByCurrentUser } = row;
  const lockedByOther = isLocked && !isLockedByCurrentUser;

  const classMapping = {
    [MAPPING_KEYS_STATUSES.validated]: lockedByOther
      ? productDefaultClass
      : "product-validated",
    [MAPPING_KEYS_STATUSES.autoDomainMapped]: lockedByOther
      ? productDefaultClass
      : "product-auto-domain-mapped",
    [MAPPING_KEYS_STATUSES.manualDomainMapped]: lockedByOther
      ? productDefaultClass
      : "product-manual-domain-mapped",
    [MAPPING_KEYS_STATUSES.manual]: lockedByOther
      ? productDefaultClass
      : "product-manual",
    [MAPPING_KEYS_STATUSES.auto]: lockedByOther
      ? productDefaultClass
      : "product-auto",
    [MAPPING_KEYS_STATUSES.rejected]: lockedByOther
      ? productDefaultClass
      : "product-rejected",
    [MAPPING_KEYS_STATUSES.reject]: lockedByOther
      ? productDefaultClass
      : "product-rejected",
    [MAPPING_KEYS_STATUSES.waitingProductCreated]: lockedByOther
      ? productDefaultClass
      : "product-created",
    [MAPPING_KEYS_STATUSES.waitingProductRequested]: lockedByOther
      ? productDefaultClass
      : "product-requested",
    [MAPPING_KEYS_STATUSES.notMapped]: lockedByOther
      ? productDefaultClass
      : "product-not-mapped",
  };

  return classMapping[value] || "default-class";
};

export const createBuyersStatusClasses = (row, value) => {
  const defaultClass = "buyer-default";

  const { isLocked, isLockedByCurrentUser } = row;
  const lockedByOther = isLocked && !isLockedByCurrentUser;

  const classMapping = {
    [BUYERS_STATUSES.brand]: lockedByOther ? defaultClass : "buyer-brand",
    [BUYERS_STATUSES.reseller]: lockedByOther ? defaultClass : "buyer-reseller",
    [BUYERS_STATUSES.exclude]: lockedByOther ? defaultClass : "buyer-exclude",
    [BUYERS_STATUSES.notMapped]: lockedByOther
      ? defaultClass
      : "buyer-not-mapped",
  };

  return classMapping[value] || "default-class";
};

export const createPathmmaticsMappingDetailsData = (mappingDetailsData) => {
  let index = 0;
  let items = [];

  mappingDetailsData.forEach((mapping) => {
    if (mapping.creativeDetails.length <= 0) {
      items.push({
        index,
        id: mapping.id,
        name: mapping.name,
        friendlyName: mapping.friendlyName,
        mediaChannelName: mapping.mediaChannelName,
        creativeDetails: {
          socialAccountId: "",
          creativeText: "",
          location: null,
          mediaTypeName: "",
          rawLocation: null,
          rawMediaTypeName: "",
        },
      });

      index++;
    } else {
      mapping.creativeDetails.forEach((creative) => {
        items.push({
          index,
          id: creative.id,
          name: mapping.name,
          friendlyName: mapping.friendlyName,
          mediaChannelName: mapping.mediaChannelName,
          creativeDetails: {
            socialAccountId: creative.socialAccountId,
            creativeText: creative.creativeText,
            location: creative.location,
            mediaTypeName: creative.mediaTypeName,
            rawLocation: creative.rawLocation,
            rawMediaTypeName: creative.rawMediaTypeName,
          },
          accountUrl: createSocialPageUrl(
            mapping.mediaChannelName,
            mapping.name,
          ),
        });

        index++;
      });
    }
  });

  return items;
};

export const createBisMappingDetailsData = (mappingDetailsData) => {
  let index = 0;
  let items = [];

  mappingDetailsData.forEach((mapping) => {
    if (mapping.creativeDetails.length <= 0) {
      items.push({
        index,
        id: mapping.id,
        name: mapping.campaignTitle,
        advertiser: mapping.advertiser,
        advertiserDomain: mapping.advertiserDomain,
        campaignTitle: mapping.campaignTitle,
        campaignUrl: mapping.campaignUrl,
        creativeDetails: {
          creativeId: "",
          firstOccurrence: "",
          location: null,
          creativeType: "",
          mediaTypeName: "",
        },
      });

      index++;
    } else {
      mapping.creativeDetails.forEach((creative) => {
        items.push({
          index,
          id: creative.id,
          name: mapping.campaignTitle,
          advertiser: mapping.advertiser,
          advertiserDomain: mapping.advertiserDomain,
          campaignTitle: mapping.campaignTitle,
          campaignUrl: mapping.campaignUrl,
          creativeDetails: {
            creativeId: creative.creativeId,
            firstOccurrence: creative.firstOccurrence,
            location: creative.location,
            creativeType: creative.creativeType,
            mediaTypeName: creative.mediaTypeName,
          },
        });

        index++;
      });
    }
  });

  return items;
};

export const createERetailMappingDetailsData = (mappingDetailsData) => {
  let index = 0;
  let items = [];

  mappingDetailsData.forEach((mapping) => {
    if (mapping.creativeDetails.length <= 0) {
      items.push({
        index,
        id: mapping.buyerId,
        creativeDetails: {
          creativeId: "",
          advertiserName: "",
          advertiserPaid: "",
          creativeLink: "",
          date: "",
          sponsoredType: "",
          title: "",
          location: null,
          mediaTypeName: "",
        },
      });

      index++;
    } else {
      mapping.creativeDetails.forEach((creative) => {
        items.push({
          index,
          id: creative.id,
          creativeDetails: {
            creativeId: creative.id,
            advertiserName: creative.advertiserName,
            advertiserPaid: creative.advertiserPaid,
            creativeLink: creative.creativeLink,
            date: creative.date,
            sponsoredType: creative.sponsoredType,
            title: creative.title,
            location: creative.location,
            mediaTypeName: creative.mediaTypeName,
          },
        });

        index++;
      });
    }
  });

  return items;
};

export const triggerDownload = (filename, data) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  if (typeof window.navigator.msSaveBlob === "function") {
    window.navigator.msSaveBlob(data, filename);
  } else {
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
  }
};

export const downloadFile = (name, extension, data) => {
  const filename = `${name}_${
    new Date()
      .toISOString()
      .replaceAll(":", "-")
      .replace("T", "_")
      .split(".")[0]
  }.${extension}`;

  triggerDownload(filename, data);
};

export const downloadTextFile = (filename, data) => {
  triggerDownload(filename, data);
};
